import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "smg-cookie-consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const onReset = () => {
      localStorage.removeItem(STORAGE_KEY);
      setVisible(true);
    };
    window.addEventListener("smg-cookie-reset", onReset);
    return () => window.removeEventListener("smg-cookie-reset", onReset);
  }, []);

  const choose = (value) => {
    localStorage.setItem(STORAGE_KEY, value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 z-[60] sm:right-auto sm:max-w-md"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="rounded-3xl border border-white/10 bg-elev/95 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl">
            <div className="flex items-start gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-volt/10 text-volt">
                <Cookie className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-base uppercase tracking-wide text-white">
                  A quick word on cookies
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">
                  We use essential cookies to run this site. With your
                  permission, we'll also use analytics to understand what
                  content helps athletes and partners most. Read our{" "}
                  <a
                    href="/privacy"
                    data-testid="cookie-privacy-link"
                    className="text-volt underline-offset-2 hover:underline"
                  >
                    privacy policy
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={() => choose("accepted")}
                data-testid="cookie-accept-btn"
                className="flex-1 rounded-full bg-volt px-6 py-3 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light"
              >
                Accept All
              </button>
              <button
                onClick={() => choose("essential")}
                data-testid="cookie-decline-btn"
                className="flex-1 rounded-full border border-white/15 px-6 py-3 font-display text-sm uppercase tracking-wider text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
              >
                Essential Only
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
